import type { FoundryV2RuntimeConnectorsRecord } from "../api/foundryV2Client";

function statusTone(status: "ready" | "watch" | "blocked"): string {
  if (status === "ready") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "watch") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-red-200 bg-red-50 text-red-700";
}

type Props = {
  runtimeConnectors: FoundryV2RuntimeConnectorsRecord | null;
  isLoading: boolean;
  error: string | null;
};

function ConnectorModeCard({
  title,
  assessment,
}: {
  title: string;
  assessment: FoundryV2RuntimeConnectorsRecord["report"]["connectors"][number]["dryRun"];
}): JSX.Element {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{title}</div>
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusTone(assessment.status)}`}>
          {assessment.status}
        </span>
      </div>
      <div className="mt-3 grid gap-2 text-xs text-slate-600 md:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
          Ready: <span className="font-semibold text-slate-900">{assessment.readyForExecution ? "yes" : "no"}</span>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
          Mutation safe: <span className="font-semibold text-slate-900">{assessment.mutationSafe ? "yes" : "no"}</span>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
          Declared env: <span className="font-semibold text-slate-900">{assessment.declaredEnvKeys.length}</span>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
          Missing env: <span className="font-semibold text-slate-900">{assessment.missingEnvKeys.length}</span>
        </div>
      </div>
      {assessment.requiredEnvKeys.length ? (
        <div className="mt-3 space-y-2 text-[11px] text-slate-600">
          <div className="font-semibold uppercase tracking-[0.14em] text-slate-500">Env contract</div>
          <div className="flex flex-wrap gap-2">
            {assessment.requiredEnvKeys.map((key) => {
              const missing = assessment.missingEnvKeys.includes(key);
              return (
                <span
                  key={`${title}-${key}`}
                  className={`rounded-full border px-2.5 py-1 font-semibold ${missing ? "border-red-200 bg-red-50 text-red-700" : "border-slate-200 bg-white text-slate-700"}`}
                >
                  {key}
                </span>
              );
            })}
          </div>
        </div>
      ) : null}
      {assessment.scopes.length ? (
        <div className="mt-3 space-y-2 text-[11px] text-slate-600">
          <div className="font-semibold uppercase tracking-[0.14em] text-slate-500">Scopes</div>
          {assessment.scopes.map((scope) => (
            <div key={`${title}-${scope.id}`} className="rounded-xl border border-slate-200 bg-white px-3 py-2">
              <div className="flex items-center justify-between gap-3">
                <span className="font-semibold text-slate-900">{scope.label}</span>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusTone(scope.status)}`}>
                  {scope.status}
                </span>
              </div>
              <div className="mt-1 text-[11px] text-slate-600">{scope.summary}</div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function FoundryRuntimeConnectorsPanel({
  runtimeConnectors,
  isLoading,
  error,
}: Props): JSX.Element {
  const report = runtimeConnectors?.report ?? null;

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f7fbff_100%)] p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)]"
      data-testid="foundry-runtime-connectors-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
            Runtime Connectors v1
          </div>
          <h3 className="mt-2 text-xl font-semibold text-slate-950">
            Enterprise connector registry with explicit dry-run posture, live mutation posture, and visible scope debt
          </h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Foundry now exposes which delivery tools are merely declared, which are safe for dry-run, and which still
            require credentials or provider-level scope verification before Jarvis is allowed to mutate anything real.
          </p>
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      {isLoading && !report ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Loading runtime connectors...
        </div>
      ) : null}

      {report ? (
        <>
          <div className="mt-5 grid gap-3 xl:grid-cols-[1.1fr_0.95fr_0.95fr_0.85fr]">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${statusTone(report.summary.dryRunStatus)}`}>
                  dry-run {report.summary.dryRunStatus}
                </span>
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${statusTone(report.summary.liveStatus)}`}>
                  live {report.summary.liveStatus}
                </span>
                <span className="text-xs text-slate-500">{report.summary.connectorCount} connector(s)</span>
              </div>
              <div className="mt-3 text-sm text-slate-700">{report.summary.headline}</div>
              <div className="mt-3 grid gap-2 text-xs text-slate-600 md:grid-cols-2">
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  Dry-run ready: {report.summary.dryRunReadyCount}
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  Dry-run blocked: {report.summary.dryRunBlockedCount}
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  Live ready: {report.summary.liveReadyCount}
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  Live watch/blocked: {report.summary.liveWatchCount + report.summary.liveBlockedCount}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Environment contract</div>
              <div className="mt-3 space-y-2 text-xs text-slate-600">
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
                  GitHub mode: <span className="font-semibold text-slate-900">{report.environment.githubMode}</span>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
                  Repository: <span className="font-semibold text-slate-900">{report.environment.repositoryName ?? "not set"}</span>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
                  Compliance: <span className="font-semibold text-slate-900">{report.environment.complianceProfile ?? "not set"}</span>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
                  Missing env keys: <span className="font-semibold text-slate-900">{report.environment.missingEnvKeys.length}</span>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Integration targets</div>
              <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-600">
                {report.environment.integrationTargets.length ? (
                  report.environment.integrationTargets.map((target) => (
                    <span key={target} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 font-semibold text-slate-700">
                      {target}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-slate-500">No explicit integration target declared.</span>
                )}
              </div>
              {report.environment.missingEnvKeys.length ? (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-xs text-red-700">
                  Missing live secrets: {report.environment.missingEnvKeys.join(", ")}
                </div>
              ) : null}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Artifacts</div>
              <div className="mt-3 space-y-2 text-xs">
                <a className="block font-medium text-blue-700 underline" href={runtimeConnectors?.artifactUrls.runtimeConnectorsReport ?? "#"}>
                  runtime_connectors_report.v1.json
                </a>
              </div>
              {report.globalNextActions.length ? (
                <div className="mt-4 space-y-2 text-[11px] text-slate-600">
                  {report.globalNextActions.slice(0, 4).map((item) => (
                    <div key={item} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      {item}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-5 space-y-4">
            {report.connectors.map((connector) => (
              <div key={connector.id} className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="max-w-3xl">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-lg font-semibold text-slate-950">{connector.label}</div>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                        {connector.deliveryTool}
                      </span>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                        {connector.family}
                      </span>
                    </div>
                    <div className="mt-2 text-sm text-slate-600">{connector.summary}</div>
                  </div>
                </div>

                <div className="mt-4 grid gap-4 xl:grid-cols-2">
                  <ConnectorModeCard title="Dry-run" assessment={connector.dryRun} />
                  <ConnectorModeCard title="Live" assessment={connector.live} />
                </div>

                <div className="mt-4 grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Contract mappings</div>
                    <div className="mt-3 space-y-2 text-xs text-slate-600">
                      {connector.contractMappings.map((mapping) => (
                        <div key={mapping.id} className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                          <div className="font-semibold text-slate-900">{mapping.label}</div>
                          <div className="mt-1">{mapping.targetSurface}</div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {mapping.contractRefs.map((ref) => (
                              <span key={`${mapping.id}-${ref}`} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                                {ref}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Health checks & notes</div>
                    <div className="mt-3 space-y-2 text-xs text-slate-600">
                      {[...connector.dryRun.healthChecks, ...connector.live.healthChecks].map((check, index) => (
                        <div key={`${connector.id}-${check.id}-${index}`} className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-semibold text-slate-900">{check.label}</span>
                            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusTone(check.status)}`}>
                              {check.status}
                            </span>
                          </div>
                          <div className="mt-1">{check.summary}</div>
                        </div>
                      ))}
                      {connector.notes.map((note) => (
                        <div key={note} className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                          {note}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}
